import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useChainId } from 'wagmi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { RoleGate } from '@/components/auth/RoleGate'
import { useConsoleRoleMembers } from '@/hooks/chain/useConsoleRoleMembers'
import { useOperatorRoleMutations } from '@/hooks/chain/useOperatorRoleMutations'
import { DEFAULT_ADMIN_ROLE, MLRO_ROLE, READ_ONLY_ROLE } from '@/config/onchain-roles'
import { chainName } from '@/config/chains'
import { isHexAddress } from '@/lib/utils'
import type { OperatorRole } from '@/types/api'

export const Route = createFileRoute('/operators')({
    component: OperatorsPage,
})

interface RoleOption {
    value: OperatorRole
    label: string
    bytes32: `0x${string}`
}

// Console RBAC roles in privilege order. `admin` is the OZ DEFAULT_ADMIN_ROLE
// and is the role-admin of the other two, so only an admin can grant/revoke.
const ROLE_OPTIONS: RoleOption[] = [
    { value: 'admin', label: 'Admin', bytes32: DEFAULT_ADMIN_ROLE },
    { value: 'mlro', label: 'MLRO', bytes32: MLRO_ROLE },
    { value: 'read_only', label: 'Read-only', bytes32: READ_ONLY_ROLE },
]

export function OperatorsPage() {
    const chainId = useChainId()
    const { admins, mlros, readOnly, isLoading } = useConsoleRoleMembers(chainId)
    const { grant, revoke, isPending, isConfirming } = useOperatorRoleMutations()
    const busy = isPending || isConfirming

    const [target, setTarget] = useState('')
    const [role, setRole] = useState<OperatorRole>('mlro')

    const trimmed = target.trim()
    const valid = isHexAddress(trimmed)
    const selected = ROLE_OPTIONS.find((o) => o.value === role)

    function onGrant() {
        if (!valid || !selected) return
        grant(selected.bytes32, trimmed as `0x${string}`)
        setTarget('')
    }

    const groups = [
        { option: ROLE_OPTIONS[0], holders: admins },
        { option: ROLE_OPTIONS[1], holders: mlros },
        { option: ROLE_OPTIONS[2], holders: readOnly },
    ]

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Operators</h1>
                <p className="text-muted-foreground text-sm">
                    On-chain console access for {chainName(chainId)}. Grants and revokes are{' '}
                    <code className="font-mono text-xs">OperatorRoles</code> transactions signed by
                    your wallet, which must hold the admin role. Everyone else gets a read-only
                    view.
                </p>
            </div>

            <RoleGate roles={['admin']}>
                <Card>
                    <CardHeader>
                        <CardTitle>Grant a console role</CardTitle>
                        <CardDescription>
                            Grant <code className="font-mono text-xs">admin</code> /{' '}
                            <code className="font-mono text-xs">mlro</code> /{' '}
                            <code className="font-mono text-xs">read_only</code> to a wallet on this
                            chain. Console access is decoupled from on-chain write roles.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[260px] flex-1 space-y-1">
                            <label
                                htmlFor="grant-address"
                                className="text-muted-foreground text-[11px] tracking-wide uppercase"
                            >
                                Wallet address
                            </label>
                            <Input
                                id="grant-address"
                                placeholder="0x…"
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1">
                            <label
                                htmlFor="grant-role"
                                className="text-muted-foreground text-[11px] tracking-wide uppercase"
                            >
                                Role
                            </label>
                            <select
                                id="grant-role"
                                aria-label="Role to grant"
                                value={role}
                                onChange={(e) => setRole(e.target.value as OperatorRole)}
                                className="border-input h-9 rounded-md border bg-transparent px-3 text-sm shadow-sm"
                            >
                                {ROLE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <Button onClick={onGrant} disabled={!valid || busy}>
                            {busy ? 'Granting…' : 'Grant role'}
                        </Button>
                    </CardContent>
                </Card>
            </RoleGate>

            {groups.map(({ option, holders }) => (
                <Card key={option.value}>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            {option.label}
                            <Badge variant="muted">{holders.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <Skeleton className="h-12 w-full" />
                        ) : holders.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No holders.</p>
                        ) : (
                            <ul className="divide-border divide-y">
                                {holders.map((h) => (
                                    <li
                                        key={h}
                                        className="flex items-center justify-between gap-3 py-2"
                                    >
                                        <AddressDisplay address={h} chainId={chainId} />
                                        <RoleGate roles={['admin']}>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={busy}
                                                onClick={() => revoke(option.bytes32, h)}
                                            >
                                                {busy ? 'Revoking…' : 'Revoke'}
                                            </Button>
                                        </RoleGate>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    )
}
